/**
 * `DesktopSidebarItem` — one row of the desktop sidebar, for any item that
 * `sidebarGroups` yields: a bottom-nav destination, a menu-sheet destination,
 * a sub-menu parent with its children, the contact action, or an off-app link.
 *
 * The current page is decided by `matchesRoute`, not by `NavLink`'s own
 * `isActive`: a sub-menu parent has no destination of its own, and Koti must
 * not match every path. A current destination carries `aria-current="page"`; a
 * sub-menu parent whose child is current carries `aria-current="true"` (the
 * current item within its set), so only one element ever announces "page".
 *
 * A greyed item is a focusable `<button aria-disabled>` that does nothing, as in
 * the bottom nav: keyboard users still land on it and hear it is unavailable.
 */

import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router';

import { useTranslate } from '../i18n/useTranslate.js';
import { useComposeWizard } from '../member-messaging/compose/wizard/ComposeWizardProvider.js';

import { ChevronDownIcon, ChevronRightIcon } from './icons.js';
import { assertNever } from './MenuSheetHelpers.js';
import { matchesChild, matchesRoute, type NavItem } from './navEntries.js';

// The prototype's desktop sidebar rows (`DesktopSidebar.tsx` at the pinned
// commit): full-width, a 2 px left rail, `py-1.5` with 24 px icons (about
// 36 px a row), and the label kept dark on the current row. This theme's `h-5`
// is the prototype's `h-6` (see `--spacing-stock-5`).
const rowClasses =
  'flex w-full items-center gap-3 border-l-2 px-4 py-1.5 text-left text-base font-medium ' +
  'no-underline transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
const activeClasses = 'border-nav-active-rail bg-tab-active/10 text-foreground';
const inactiveClasses =
  'border-transparent text-foreground/80 cursor-pointer hover:bg-foreground/4 hover:text-foreground';
const greyedClasses = 'border-transparent text-foreground/80 opacity-50 cursor-not-allowed';
// Sub-menu children are indented, rounded chips without a rail or an icon.
const childGroupClasses = 'ml-9 mr-2 flex flex-col gap-0.5 py-1';
const childRowClasses =
  'flex items-center rounded-lg px-3 py-2 text-base no-underline transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';
const childActiveClasses = 'bg-tab-active/10 font-medium text-foreground';
const childInactiveClasses =
  'text-foreground/80 cursor-pointer hover:bg-foreground/4 hover:text-foreground';
const iconClasses = 'h-5 w-5 shrink-0';
// The pill sits right after the label, as in the prototype.
const labelClasses = 'inline-flex min-w-0 items-center gap-1.5';
const chevronClasses = 'ml-auto h-4 w-4 shrink-0';
// `text-xs` stands in for the prototype's 11 px.
const unreadPillClasses =
  'inline-flex h-stock-5 min-w-stock-5 items-center justify-center rounded-full bg-unread-pill px-1.5 ' +
  'text-xs font-bold leading-none text-unread-pill-foreground';

const UNREAD_DISPLAY_CAP = 99;

/** The sidebar's unread pill text: the count, capped at the prototype's `99+`. */
export function unreadDisplay(count: number): string {
  return count > UNREAD_DISPLAY_CAP ? `${String(UNREAD_DISPLAY_CAP)}+` : String(count);
}

function stateClasses(active: boolean): string {
  return `${rowClasses} ${active ? activeClasses : inactiveClasses}`;
}

function UnreadPill({ count }: { readonly count: number }): JSX.Element | null {
  const t = useTranslate();
  if (count <= 0) return null;
  return (
    <span className={unreadPillClasses}>
      <span aria-hidden="true">{unreadDisplay(count)}</span>
      <span className="sr-only">
        {t('shell.primaryNav.unreadBadge').replace('{n}', String(count))}
      </span>
    </span>
  );
}

export interface DesktopSidebarItemProps {
  readonly item: NavItem;
  readonly pathname: string;
  readonly greyed: boolean;
  /** Viestit's unread count; 0 for every other item. */
  readonly unreadCount: number;
  /** Omat tiedot's unpaid-fee marker; absent for every other item. */
  readonly marker?: ReactNode;
  /** Sub-menu parents only: whether the sub-menu is open, and its toggle. */
  readonly isOpen?: boolean;
  readonly onToggle?: () => void;
  readonly submenuId?: string;
}

export function DesktopSidebarItem({
  item,
  pathname,
  greyed,
  unreadCount,
  marker,
  isOpen = false,
  onToggle,
  submenuId,
}: DesktopSidebarItemProps): JSX.Element {
  const t = useTranslate();
  const composeWizard = useComposeWizard();
  const { entry } = item;
  const label = t(entry.labelKey);
  const content: ReactNode = (
    <>
      <entry.Icon aria-hidden="true" className={iconClasses} />
      <span className={labelClasses}>
        <span className="truncate">{label}</span>
        <UnreadPill count={unreadCount} />
        {marker}
      </span>
    </>
  );

  if (greyed) {
    return (
      <button type="button" aria-disabled="true" className={`${rowClasses} ${greyedClasses}`}>
        {content}
      </button>
    );
  }

  const active = matchesRoute(pathname, item);

  if (item.source === 'nav') {
    return (
      <Link
        to={item.entry.path}
        aria-current={active ? 'page' : undefined}
        className={stateClasses(active)}
      >
        {content}
      </Link>
    );
  }

  const service = item.entry;
  switch (service.kind) {
    case 'flat-link':
      return (
        <Link
          to={service.to}
          aria-current={active ? 'page' : undefined}
          className={stateClasses(active)}
        >
          {content}
        </Link>
      );
    case 'action':
      return (
        <button
          type="button"
          className={stateClasses(false)}
          onClick={() => {
            switch (service.actionId) {
              case 'open-compose-wizard':
                composeWizard.open();
                return;
              default:
                assertNever(service.actionId);
            }
          }}
        >
          {content}
        </button>
      );
    case 'external-link':
      return (
        <a
          href={service.href}
          target="_blank"
          rel="noopener noreferrer"
          className={stateClasses(false)}
        >
          {content}
          <span className="sr-only">{`(${t('shell.desktopSidebar.opensInNewTab')})`}</span>
        </a>
      );
    case 'expandable':
      return (
        <div>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={submenuId}
            aria-current={active ? 'true' : undefined}
            onClick={onToggle}
            className={stateClasses(active)}
          >
            {content}
            {isOpen ? (
              <ChevronDownIcon aria-hidden="true" className={chevronClasses} />
            ) : (
              <ChevronRightIcon aria-hidden="true" className={chevronClasses} />
            )}
          </button>
          {/* biome-ignore lint/a11y/useSemanticElements: WAI Disclosure pattern, as in MenuSheet — the sub-menu region is an ARIA group, not a form fieldset. */}
          <div
            role="group"
            id={submenuId}
            hidden={!isOpen}
            aria-label={`${label} ${t('shell.menu.submenuSuffix')}`}
            className={childGroupClasses}
          >
            {service.children.map((child) => {
              const childActive = matchesChild(pathname, child);
              return (
                <Link
                  key={child.key}
                  to={child.to}
                  aria-current={childActive ? 'page' : undefined}
                  className={`${childRowClasses} ${childActive ? childActiveClasses : childInactiveClasses}`}
                >
                  {t(child.labelKey)}
                </Link>
              );
            })}
          </div>
        </div>
      );
    default:
      return assertNever(service);
  }
}
